import React, { useState, useEffect, useRef, useContext } from 'react';
import {
    Box,
    AppBar,
    Typography,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListItemIcon,
    Divider
} from '@mui/material';
import {
    Home as HomeIcon,
    AccountCircle as ProfileIcon,
    LibraryBooks as TestIcon,
    CardGiftcardOutlined,
    Settings as SettingsIcon,
    Description as TermsIcon,
    Lock as PrivacyIcon,
    Article as NewsIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import logoImg from '../assets/img/header/logo.svg';
import { SvgIcon } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import TestSkillsDialog from '../components/common/TestSkillsDialog';
import { getUiLang, setUiLang } from '../utils/uiLanguage';
import copy from '../features/home/homeCopy';
import ProfileMenu from '../components/common/ProfileMenu';
import { getProfile } from '../apiCalls/user_profile/api_v1_profile';
import { loadActiveStreak } from '../apiCalls/streaks/api_v1_streaks';
import { refreshEasterWallet } from '../apiCalls/economy/api_v1_economy';
import {
    formFromProfile,
    mergeProfiles,
    readSessionProfile,
    saveProfileRecord,
    unwrapProfile,
} from '../utils/profileForm';
import { clearLegacyProfileLocalStorage } from '../utils/profileUserStore';
import {
    EggWalletChip,
    LangSwitch,
    AvatarButton,
    getEggBalance,
} from '../components/ui/GaPrimitives';
import { AuthContext } from '../apiCalls/jwt/AuthContext';

const BackIcon = () => (
    <SvgIcon sx={{ fontSize: '24px' }}>
        <svg width="21" height="22" viewBox="0 0 21 22" fill="none">
            <path d="M21 11C21 13.0767 20.3842 15.1068 19.2304 16.8335C18.0767 18.5602 16.4368 19.906 14.5182 20.7007C12.5996 21.4955 10.4884 21.7034 8.45155 21.2982C6.41475 20.8931 4.54383 19.8931 3.07538 18.4246C1.60693 16.9562 0.606899 15.0852 0.201756 13.0484C-0.20339 11.0116 0.00454521 8.90045 0.799265 6.98182C1.59398 5.0632 2.9398 3.42332 4.66651 2.26957C6.39323 1.11581 8.4233 0.5 10.5 0.5C13.2848 0.5 15.9555 1.60625 17.9246 3.57538C19.8938 5.54451 21 8.21523 21 11ZM16.5 10.25H7.3875L11.5725 6.04475L10.5 5L4.5 11L10.5 17L11.5725 15.9298L7.3875 11.75H16.5V10.25Z" fill="#FAAE1C" />
        </svg>
    </SvgIcon>
);

function LayoutMobile({ children }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { auth } = useContext(AuthContext);
    const queryParams = new URLSearchParams(location.search);
    const gameUuid = queryParams.get('game');
    const [value, setValue] = useState(location.pathname);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [openTestSkills, setOpenTestSkills] = useState(false);
    const [lang, setLang] = useState(getUiLang());
    const [profile, setProfile] = useState(null);
    const streakSynced = useRef(false);
    const theme = useTheme();
    const t = copy[lang];
    const isMainChrome = !['/game'].includes(location.pathname);

    const switchLang = (next) => {
        setUiLang(next);
        setLang(next);
    };

    useEffect(() => {
        setValue(location.pathname);
        if (location.state && location.state.openProfileMenu) {
            setIsSidebarOpen(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.pathname, location.state, navigate]);

    // Bootstraps when auth token is ready — avoids 401 then retry duplicates.
    useEffect(() => {
        let cancelled = false;
        clearLegacyProfileLocalStorage();

        const applySession = () => {
            try {
                const sessionProfile = readSessionProfile();
                if (!sessionProfile || cancelled) return;
                setProfile(formFromProfile(sessionProfile));
            } catch {
                /* keep current profile */
            }
        };

        applySession();

        const token = auth?.accessToken || sessionStorage.getItem("accessToken");
        if (!token) {
            const handleProfileUpdate = () => applySession();
            window.addEventListener('profileUpdated', handleProfileUpdate);
            return () => {
                cancelled = true;
                window.removeEventListener('profileUpdated', handleProfileUpdate);
            };
        }

        (async () => {
            try {
                const fetched = await getProfile();
                if (cancelled) return;
                const apiProfile = unwrapProfile(fetched);
                if (apiProfile) {
                    const profileData = saveProfileRecord(
                        mergeProfiles(apiProfile, readSessionProfile()),
                        { notify: false },
                    );
                    setProfile(profileData);
                }
            } catch (e) {
                console.error("Failed to load profile", e);
                applySession();
            }

            if (streakSynced.current) {
                applySession();
                return;
            }
            streakSynced.current = true;

            try {
                await loadActiveStreak();
            } catch (error) {
                console.error("Active streak load failed:", error);
            }

            if (!cancelled) applySession();
        })();

        const handleProfileUpdate = () => applySession();
        window.addEventListener('profileUpdated', handleProfileUpdate);
        return () => {
            cancelled = true;
            window.removeEventListener('profileUpdated', handleProfileUpdate);
        };
    }, [auth?.accessToken]);

    // Keep egg wallet fresh: force /economy/easters on every route that shows the chip.
    useEffect(() => {
        if (!isMainChrome) return undefined;
        if (!sessionStorage.getItem("accessToken")) return undefined;

        let cancelled = false;
        (async () => {
            try {
                await refreshEasterWallet();
                if (cancelled) return;
                const sessionProfile = readSessionProfile();
                if (sessionProfile) setProfile(formFromProfile(sessionProfile));
            } catch (error) {
                console.error("Easter wallet refresh failed:", error);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [location.pathname, isMainChrome]);

    const handleChange = (event, newValue) => {
        setValue(newValue);
        navigate(newValue);
    };

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const handleNavigation = (action) => {
        if (typeof action === 'function') {
            action();
        } else if (typeof action === 'string') {
            navigate(action);
        }
        setIsSidebarOpen(false);
    };

    const menuItems = [
        { icon: HomeIcon, label: "Home", action: '/' },
        { icon: NewsIcon, label: "News", action: '/news' },
        { icon: ProfileIcon, label: "Profile", action: '/profile' },
        {
            icon: TestIcon,
            label: "Test your Skills",
            action: () => setOpenTestSkills(true)
        },
        {
            icon: CardGiftcardOutlined,
            label: "Win a Gift",
            action: '/play'
        },
        { type: 'divider' },
        // { icon: SettingsIcon, label: "Settings", action: '/settings' },
        { icon: TermsIcon, label: "Terms of Service", action: '/terms' },
        // { icon: PrivacyIcon, label: "Privacy Policy", action: '/privacy' }
    ];

    return (
        <Box sx={{
            height: '100%',
            maxHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: '#0D0F0A',
        }}>
            {location.pathname !== '/bites' && location.pathname !== '/ga-fps-game' && (
                <AppBar
                    position="relative"
                    sx={{
                        bgcolor: '#0D0F0A',
                        boxShadow: 'none',
                        flexShrink: 0,
                        zIndex: theme.zIndex.appBar,
                        maxHeight: '60px',
                        minHeight: '60px',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0 16px',
                        borderBottom: '1px solid #262B1C'
                    }}
                >
                    {location.pathname === '/game' ?
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <ArrowBackRounded
                                onClick={() => {
                                    if (window.history.length > 1) {
                                        navigate(-1);
                                    } else {
                                        navigate('/');
                                    }
                                }}
                                sx={{ cursor: 'pointer', color: 'white' }}
                            />
                            <Typography variant="h6" sx={{ textAlign: 'left', color: 'white', fontFamily: "'Azonix', sans-serif" }}>{gameUuid}</Typography>
                        </Box>
                        : (location.pathname === '/guides' || location.pathname.startsWith('/news') || location.pathname === '/rewards' || location.pathname === '/profile' || location.pathname === '/puzzle' || location.pathname === '/article-view') ?
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box
                                    onClick={() => {
                                        if (location.state?.fromProfileMenu) {
                                            navigate('/', { state: { openProfileMenu: true } });
                                        } else if (window.history.length > 1) {
                                            navigate(-1);
                                        } else {
                                            navigate('/');
                                        }
                                    }}
                                    sx={{
                                        width: '48px',
                                        height: '56px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: '#B8EE3C',
                                        clipPath: 'polygon(0 0, calc(100% - 12px) 0, 100% 12px, 100% 100%, 0 100%)',
                                        cursor: 'pointer'
                                    }}
                                >
                                    <svg width="12" height="20" viewBox="0 0 12 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M10 2L2 10L10 18" stroke="#0D0F0A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </Box>
                            </Box>
                            :
                            <Box
                                onClick={() => { navigate('/'); }}
                                sx={{
                                    width: 107,
                                    height: 43,
                                    p: '6px 8px',
                                    boxSizing: 'border-box',
                                    bgcolor: '#B8EE3C',
                                    cursor: 'pointer',
                                }}
                            >
                                <Box
                                    component="img"
                                    src={logoImg}
                                    alt="GamezAcademy"
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                        filter: 'brightness(0)',
                                    }}
                                />
                            </Box>
                    }
                    {isMainChrome && (
                        <>
                            <Box sx={{
                                position: 'absolute',
                                left: '50%',
                                transform: 'translateX(-50%)',
                            }}>
                                <EggWalletChip count={getEggBalance(profile)} onClick={() => navigate('/rewards')} />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', ml: 'auto', mr: '8px' }}>
                                <LangSwitch lang={lang} switchLang={switchLang} />
                            </Box>
                        </>
                    )}
                    <AvatarButton src={profile?.profileImage} onClick={toggleSidebar} size={34} />
                </AppBar>
            )}

            <Box
                id="main-scroll-container"
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: location.pathname === '/puzzle' ? 'hidden' : 'auto',
                    display: location.pathname === '/puzzle' ? 'flex' : 'block',
                    flexDirection: 'column',
                    backgroundColor: '#0D0F0A',
                    '&::-webkit-scrollbar': {
                        display: 'none',
                    },
                    scrollbarWidth: 'none',
                    ...(location.pathname === '/puzzle'
                        ? { '& > *': { flex: 1, minHeight: 0 } }
                        : null),
                }}>
                {children}
            </Box>

            <Drawer
                anchor="right"
                open={isSidebarOpen}
                onClose={toggleSidebar}
                PaperProps={{
                    sx: {
                        width: '100%',
                        maxWidth: '100vw',
                        bgcolor: '#0D0F0A'
                    }
                }}
            >
                <ProfileMenu
                    onClose={toggleSidebar}
                    lang={lang}
                    switchLang={switchLang}
                    profile={profile}
                />
            </Drawer>

            <TestSkillsDialog
                open={openTestSkills}
                onClose={() => setOpenTestSkills(false)}
            />

            {(isMainChrome && location.pathname !== '/bites' && location.pathname !== '/ga-fps-game') && (
                <Box sx={{
                    height: 62,
                    flexShrink: 0,
                    bgcolor: '#0D0F0A',
                    borderTop: '1px solid #1A1E14',
                    display: 'flex',
                    alignItems: 'center',
                    zIndex: theme.zIndex.appBar,
                }}>
                    {[
                        { label: t.bottom[0], path: '/' },
                        { label: t.bottom[1], path: '/play' },
                        { label: t.bottom[2], path: '/guides' },
                        { label: t.bottom[3], path: '/news' },
                        { label: t.bottom[4], path: '/rewards' },
                    ].map((tab) => {
                        const active = location.pathname === tab.path || (tab.path === '/' && location.pathname === '/home') || (tab.path === '/news' && location.pathname.startsWith('/news'));
                        const eggCount = getEggBalance(profile);
                        return (
                            <Box
                                key={tab.path}
                                sx={{
                                    flex: 1,
                                    height: 36,
                                    position: 'relative',
                                }}
                            >
                                <Box
                                    component="button"
                                    type="button"
                                    aria-label={tab.label}
                                    aria-current={active ? "page" : undefined}
                                    onClick={() => navigate(tab.path)}
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        bgcolor: active ? '#B8EE3C' : 'transparent',
                                        clipPath: active ? 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 0 100%)' : 'none',
                                        border: 'none',
                                        p: 0,
                                    }}
                                >
                                    <Typography sx={{
                                        fontFamily: "'Rajdhani', sans-serif",
                                        fontWeight: 700,
                                        fontSize: 14,
                                        letterSpacing: '0.5px',
                                        color: active ? '#0D0F0A' : '#666B5C',
                                    }}>
                                        {tab.label}
                                    </Typography>
                                </Box>
                                {tab.path === '/rewards' && (
                                    <Box sx={{
                                        position: 'absolute',
                                        right: 2,
                                        top: -8,
                                        minWidth: 18,
                                        height: 16,
                                        px: '6px',
                                        bgcolor: '#F9A825',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '10px',
                                        zIndex: 2,
                                        pointerEvents: 'none',
                                    }}>
                                        <Typography sx={{
                                            fontFamily: "'Poppins', sans-serif",
                                            fontWeight: 700,
                                            fontSize: 10,
                                            color: '#141414',
                                            lineHeight: 1,
                                        }}>{eggCount}</Typography>
                                    </Box>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}
        </Box>
    );
}

export default LayoutMobile;